import { Fragment, useEffect, useRef, useState } from 'react'
import Konva from 'konva'
import { Arc, Group, Image as KonvaImage, Line, Rect } from 'react-konva'
import {
  DEFAULT_ICON_STROKE_COLOR,
  DEFAULT_ICON_STROKE_WIDTH,
  DEFAULT_STROKE_COLOR,
} from '../constants'
import {
  assignedIconSrcFor,
  getIconCanvasFrame,
  getIconImageSrc,
} from '../iconKinds'
import { isEncounterGateway } from '../encounterCreatureIcons'
import { ShapeInteraction } from './shapeInteraction'
import { IconShape } from './iconShapes'
import { ResolvedEntityBadge } from './entityAssignmentMatching'
import IconEntityBadge from './IconEntityBadge'

type Props = {
  shape: IconShape
  width: number
  height: number
  interaction?: ShapeInteraction
  showEntityBadge?: boolean
  zoomScale?: number
  entityStartNumber?: number
  entityAssignments?: Map<number, ResolvedEntityBadge>
  encounterId?: number
  showText?: boolean
}

const cornerRadiusFor = (
  corner: 'circle' | 'rounded',
  pixelWidth: number,
  pixelHeight: number,
) => {
  const minSide = Math.min(pixelWidth, pixelHeight)
  if (corner === 'circle') return minSide / 2
  return minSide * 0.25
}

type EncounterDirectionPointerProps = {
  pixelWidth: number
  pixelHeight: number
  stroke: string
  strokeWidth: number
  opacity: number
}

const EncounterDirectionPointer = ({
  pixelWidth,
  pixelHeight,
  stroke,
  strokeWidth,
  opacity,
}: EncounterDirectionPointerProps) => {
  const radius = Math.min(pixelWidth, pixelHeight) / 2
  const arcThickness = Math.max(2, Math.min(pixelWidth, pixelHeight) * 0.045)
  const gap = Math.max(2, strokeWidth * 0.75)
  const innerRadius = radius + gap
  const outerRadius = innerRadius + arcThickness
  const sweep = 110
  const tipSize = Math.max(
    arcThickness * 2.4,
    Math.min(pixelWidth, pixelHeight) * 0.12,
  )
  const tipY = outerRadius + tipSize * 0.05

  return (
    <Group
      x={pixelWidth / 2}
      y={pixelHeight / 2}
      listening={false}
      opacity={opacity}
    >
      <Arc
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        angle={sweep}
        rotation={90 - sweep / 2}
        fill={stroke}
        shadowColor={stroke}
        shadowBlur={3}
        shadowOpacity={0.25}
        shadowEnabled
      />
      <Line
        points={[
          0,
          tipY + tipSize * 0.65,
          -tipSize * 0.85,
          tipY - tipSize * 0.2,
          tipSize * 0.85,
          tipY - tipSize * 0.2,
        ]}
        closed
        fill={stroke}
        lineJoin='round'
        shadowColor={stroke}
        shadowBlur={2}
        shadowOpacity={0.25}
        shadowEnabled
      />
    </Group>
  )
}

const syncNodeTransform = (
  source: Konva.Group,
  target: Konva.Group,
  rotation: number,
) => {
  target.x(source.x())
  target.y(source.y())
  target.scaleX(source.scaleX())
  target.scaleY(source.scaleY())
  target.offsetX(source.offsetX())
  target.offsetY(source.offsetY())
  target.rotation(rotation)
}

const IconShapeView = ({
  shape,
  width,
  height,
  interaction,
  showEntityBadge = false,
  zoomScale = 1,
  entityStartNumber = 1,
  entityAssignments,
  encounterId,
  showText = true,
}: Props) => {
  const handleRef = useRef<Konva.Group | null>(null)
  const visualRef = useRef<Konva.Group | null>(null)
  const pointerRef = useRef<Konva.Group | null>(null)
  const [image, setImage] = useState<HTMLImageElement>()
  const frame = getIconCanvasFrame(shape.kind, shape.value)
  const assignedIconSrc = showEntityBadge
    ? assignedIconSrcFor(shape, entityAssignments?.get(entityStartNumber))
    : undefined
  const imageSrc =
    assignedIconSrc ?? getIconImageSrc(shape.kind, shape.value, encounterId)
  const isEncounter =
    shape.kind === 'encounter' && !isEncounterGateway(shape.value)
  const rotation = shape.rotation ?? 0

  useEffect(() => {
    setImage(undefined)
    if (!imageSrc) return

    const element = new window.Image()
    element.src = imageSrc
    const handleLoad = () => setImage(element)
    element.addEventListener('load', handleLoad)
    return () => element.removeEventListener('load', handleLoad)
  }, [imageSrc])

  const centerX = shape.x * width
  const centerY = shape.y * height
  const pixelWidth = shape.width * width
  const pixelHeight = shape.height * height
  const opacity = shape.opacity ?? 1
  const cornerRadius = frame
    ? cornerRadiusFor(frame.corner, pixelWidth, pixelHeight)
    : 0
  const strokeWidth = shape.stroke_width ?? DEFAULT_ICON_STROKE_WIDTH
  const stroke =
    shape.stroke_rgb ??
    (isEncounter ? DEFAULT_STROKE_COLOR : DEFAULT_ICON_STROKE_COLOR)

  const syncEncounterOverlays = () => {
    if (!isEncounter) return
    const handle = handleRef.current
    if (!handle) return
    if (visualRef.current) syncNodeTransform(handle, visualRef.current, 0)
    if (pointerRef.current) {
      syncNodeTransform(handle, pointerRef.current, handle.rotation())
    }
  }

  const setHandleRef = (node: Konva.Group | null) => {
    handleRef.current = node
    interaction?.nodeRef(node)
  }

  if (!isEncounter) {
    return (
      <Group
        ref={interaction?.nodeRef}
        x={centerX}
        y={centerY}
        width={pixelWidth}
        height={pixelHeight}
        offsetX={pixelWidth / 2}
        offsetY={pixelHeight / 2}
        rotation={rotation}
        draggable={interaction?.draggable ?? false}
        listening={interaction !== undefined}
        onMouseEnter={interaction?.onMouseEnter}
        onMouseLeave={interaction?.onMouseLeave}
        onMouseDown={interaction?.onMouseDown}
        onTap={interaction?.onTap}
        onDragStart={interaction?.onDragStart}
        onDragMove={interaction?.onDragMove}
        onDragEnd={interaction?.onDragEnd}
      >
        <KonvaImage
          image={image}
          width={pixelWidth}
          height={pixelHeight}
          cornerRadius={cornerRadius}
          opacity={opacity}
        />
        {frame ? (
          <Rect
            width={pixelWidth}
            height={pixelHeight}
            cornerRadius={cornerRadius}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeScaleEnabled={false}
            opacity={opacity}
            listening={false}
          />
        ) : null}
        {showEntityBadge ? (
          <IconEntityBadge
            shape={shape}
            width={width}
            height={height}
            zoomScale={zoomScale}
            startNumber={entityStartNumber}
            entityAssignments={entityAssignments}
            showText={showText}
            hasAssignedIcon={!!assignedIconSrc}
          />
        ) : null}
      </Group>
    )
  }

  return (
    <Fragment>
      <Group
        ref={setHandleRef}
        x={centerX}
        y={centerY}
        width={pixelWidth}
        height={pixelHeight}
        offsetX={pixelWidth / 2}
        offsetY={pixelHeight / 2}
        rotation={rotation}
        draggable={interaction?.draggable ?? false}
        listening={interaction !== undefined}
        onMouseEnter={interaction?.onMouseEnter}
        onMouseLeave={interaction?.onMouseLeave}
        onMouseDown={interaction?.onMouseDown}
        onTap={interaction?.onTap}
        onDragStart={interaction?.onDragStart}
        onDragMove={(e) => {
          syncEncounterOverlays()
          interaction?.onDragMove(e)
        }}
        onDragEnd={(e) => {
          syncEncounterOverlays()
          interaction?.onDragEnd(e)
        }}
        onTransform={syncEncounterOverlays}
        onTransformEnd={syncEncounterOverlays}
      >
        <Rect width={pixelWidth} height={pixelHeight} opacity={0} listening />
      </Group>
      <Group
        ref={visualRef}
        x={centerX}
        y={centerY}
        width={pixelWidth}
        height={pixelHeight}
        offsetX={pixelWidth / 2}
        offsetY={pixelHeight / 2}
        rotation={0}
        listening={false}
      >
        <KonvaImage
          image={image}
          width={pixelWidth}
          height={pixelHeight}
          cornerRadius={cornerRadius}
          opacity={opacity}
        />
        <Rect
          width={pixelWidth}
          height={pixelHeight}
          cornerRadius={cornerRadius}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeScaleEnabled={false}
          opacity={opacity}
          listening={false}
        />
        {showEntityBadge ? (
          <IconEntityBadge
            shape={shape}
            width={width}
            height={height}
            zoomScale={zoomScale}
            startNumber={entityStartNumber}
            entityAssignments={entityAssignments}
            showText={showText}
            hasAssignedIcon={!!assignedIconSrc}
          />
        ) : null}
      </Group>
      <Group
        ref={pointerRef}
        x={centerX}
        y={centerY}
        width={pixelWidth}
        height={pixelHeight}
        offsetX={pixelWidth / 2}
        offsetY={pixelHeight / 2}
        rotation={rotation}
        listening={false}
      >
        <EncounterDirectionPointer
          pixelWidth={pixelWidth}
          pixelHeight={pixelHeight}
          stroke={stroke}
          strokeWidth={strokeWidth}
          opacity={opacity}
        />
      </Group>
    </Fragment>
  )
}

export default IconShapeView
